import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { HeadLine } from "../HeadLine";
import {
  fetchGeneralInformation,
  fetchSellingGiftCards,
  fetchMerchantInformation,
  fetchOrdersAndPayments,
  fetchBadCardsLiabilitiesAndDeductions,
} from "./thunk";

const QAGenerator = ({ data }) => {
  const [currentIdx, setCurrentIdx] = useState();
  const [previousIdx, setPreviousIdx] = useState();
  const [currentState, setCurrentState] = useState(false);
  const [previousState, setPreviousState] = useState(false);
  const mediaPhone = `@media (max-width: 767px)`;
  const mediaDesktop = `@media (min-width: 768px)`;
  const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
  `;

  const Content = styled.div`
    transition: margin 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    margin-left: auto;
    margin-right: auto;
    box-shadow: ${({ isOpen }) =>
      isOpen ? "0 10px 10px 0 rgba(119, 121, 132, 0.1)" : "none"};
    border-radius: 5px;
    width: 80%;
    margin-bottom: 15px;
    margin-top: 15px;
  `;

  const QuestionContent = styled.div`
    background-color: ${({ isOpen }) => (isOpen ? "#FFFFFF" : "#F6F7F6")};
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 5px;
    padding: 0 24px 0 24px;
    transition: min-height 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
      background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    ${mediaDesktop} {
      height: 75px;
      min-height: 65px;
    }
    ${mediaPhone} {
      height: 55px;
      min-height: 45px;
    }
  `;

  const Question = styled.p`
    font-weight: 500;
    ${mediaDesktop} {
      font-size: 15px;
    }

    ${mediaPhone} {
      font-size: 12px;
    }
  `;

  const AnswerContent = styled.div`
    display: ${({ isOpen }) => (isOpen ? "block" : "none")};
    background-color: #ffffff;
    border-top: 2px solid #edeeee;
    transition-duration: 500ms;
    border-radius: 5px;
    height: auto;
    margin-top: 2px;
    padding: 25px;
    transition: height 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    ${mediaDesktop} {
      min-height: 48px;
    }
    ${mediaPhone} {
      min-height: 30px;
    }
  `;

  const Answer = styled.p`
    color: #777984;
    line-height: 1.88;

    ${mediaDesktop} {
      font-size: 14px;
    }

    ${mediaPhone} {
      font-size: 12px;
    }
  `;

  const Icon = styled.span`
    background-color: transparent;
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    padding: 12px;
    overflow: visible;
    font-size: 1.5rem;
    text-align: center;
    border-radius: 50%;
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};

    .expand-icon {
      width: 21px;
      color: #1f2021;
      fill: currentColor;
      height: 1em;
      display: inline-block;
      font-size: 50px;
      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      flex-shrink: 0;
      user-select: none;
    }
  `;

  const stateToggle = (idx) => {
    if (previousIdx) {
      setPreviousState(false);
    }

    if (currentIdx === idx) {
      setCurrentState(false);
      setCurrentIdx();
    } else {
      setPreviousIdx(currentIdx);
      setCurrentIdx(idx);
      setCurrentState(true);
    }
  };

  const currentIndexState = (idx) => {
    if (previousIdx === idx) {
      return previousState;
    }

    if (currentIdx === idx) {
      return currentState;
    }

    return false;
  };

  const buildContentId = (index, question) =>
    `question${index}-${question
      .toLowerCase()
      .slice(0, 15)
      .replace(/\s+/g, "")}`;

  const refs = data.reduce((acc, value, idx) => {
    acc[idx] = React.createRef();
    return acc;
  }, {});

  return (
    <>
      <Container>
        {data.map(({ question, answer }, index) => (
          <Content
            key={index}
            isOpen={currentIndexState(index)}
            id={buildContentId(index, question)}
            ref={refs[index]}
          >
            <QuestionContent
              onClick={() => stateToggle(index)}
              isOpen={currentIndexState(index)}
            >
              <Question>{question}</Question>
              <Icon isOpen={currentIndexState(index)}>
                <svg
                  className="expand-icon"
                  focusable="false"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="presentation"
                >
                  <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
                </svg>
              </Icon>
            </QuestionContent>
            <AnswerContent isOpen={currentIndexState(index)}>
              <Answer
                dangerouslySetInnerHTML={{
                  __html: answer,
                }}
              />
            </AnswerContent>
          </Content>
        ))}
      </Container>
    </>
  );
};

QAGenerator.propTypes = {
  data: PropTypes.array.isRequired,
};

const FAQContent = () => {
  /** data **/
  const GENERAL_INFORMATION_DATA = fetchGeneralInformation();
  const SELLING_GIFT_CARDS_DATA = fetchSellingGiftCards();
  const MERCHANT_INFORMATION_DATA = fetchMerchantInformation();
  const ORDERS_AND_PAYMENTS_DATA = fetchOrdersAndPayments();
  const BAD_CARDS_LIABILITIES_DEDUCTIONS_DATA = fetchBadCardsLiabilitiesAndDeductions();

  /** refs **/
  const GENERAL_INFORMATION_REF = useRef();
  const SELLING_GIFT_CARDS_REF = useRef();
  const MERCHANT_INFORMATION_REF = useRef();
  const ORDERS_AND_PAYMENTS_REF = useRef();
  const BAD_CARDS_LIABILITIES_DEDUCTIONS_REF = useRef();
  const CONTENT_VIEW_REF = useRef();
  const scrollTopPosition = useRef(0);

  /** state **/
  const [generalItemActive, setGeneralItemActive] = useState(true);
  const [sellItemActive, setSellItemActive] = useState(false);
  const [merchantActive, setMerchantItemActive] = useState(false);
  const [ordersActive, setOrdersItemActive] = useState(false);
  const [badCardsActive, setBadCardsItemActive] = useState(false);

  /** css **/
  const Container = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-around;
  `;
  const Menu = styled.div`
    width: 250px;
    background: #ffffff;
    box-shadow: 0 0 50px rgba(31, 32, 33, 0.08);
    justify-content: space-around;
    height: 413px;
  `;
  const Content = styled.div`
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow-y: auto;
    width: 656px;
    max-height: ${({ height }) => `${height}px`};
  `;
  const Line = styled.div`
    background: #c7a878;
    border-radius: 1.5px;
    width: 36px;
    height: 3px;
    margin: 2rem auto;
  `;
  const Header = styled.div`
    text-align: center;
    margin: 2rem 0;
  `;
  const Item = styled.div`
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    color: #1f2021;
    padding: 2rem;
    border-bottom: 1px solid #e1eaed;
    text-align: center;
    &:hover {
      text-decoration: none;
      color: #1f2021;
    }
    &:last-of-type {
      border-bottom: none;
    }
  `;
  const ActiveItem = styled.div`
    background: #c7a878;
    border-radius: 0 0 100px 100px;
    transform: rotate(-90deg);
    width: 32px;
    height: 3px;
    position: relative;
    left: -3rem;
    top: 1rem;
  `;

  useEffect(() => {
    if (scrollTopPosition.current > 0) {
      CONTENT_VIEW_REF.current.scrollTop = scrollTopPosition.current;
      CONTENT_VIEW_REF.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [scrollTopPosition.current]);

  const setContentFocus = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView(true);
    }
  };

  const onScroll = () => {
    if (CONTENT_VIEW_REF.current) {
      const { scrollTop, scrollHeight } = CONTENT_VIEW_REF.current;
      scrollTopPosition.current = scrollTop >= 1428 ? scrollHeight : scrollTop;
      setGeneralItemActive(scrollTop <= 420);
      setSellItemActive(scrollTop >= 420 && scrollTop <= 765);
      setMerchantItemActive(scrollTop >= 766 && scrollTop <= 1390);
      setOrdersItemActive(scrollTop >= 1391 && scrollTop <= 1427);
      setBadCardsItemActive(scrollTop >= 1428 && scrollTop <= scrollHeight);
    }
  };

  return (
    <Container>
      <Menu>
        <Item onClick={() => setContentFocus(GENERAL_INFORMATION_REF)}>
          {generalItemActive && <ActiveItem />}
          General Information
        </Item>
        <Item onClick={() => setContentFocus(SELLING_GIFT_CARDS_REF)}>
          {sellItemActive && <ActiveItem />}
          Selling Gift Cards
        </Item>
        <Item onClick={() => setContentFocus(MERCHANT_INFORMATION_REF)}>
          {merchantActive && <ActiveItem />}
          Merchant Information
        </Item>
        <Item onClick={() => setContentFocus(ORDERS_AND_PAYMENTS_REF)}>
          {ordersActive && <ActiveItem />}
          Orders & Payments
        </Item>
        <Item
          onClick={() => setContentFocus(BAD_CARDS_LIABILITIES_DEDUCTIONS_REF)}
        >
          {badCardsActive && <ActiveItem />}
          Bad Cards, Liabilities, & Deductions
        </Item>
      </Menu>
      <Content
        height={window.innerHeight - 140}
        ref={CONTENT_VIEW_REF}
        onScroll={() => onScroll()}
      >
        <Line ref={GENERAL_INFORMATION_REF} />
        <Header>
          <HeadLine headingText={"General Information"} />
        </Header>
        <QAGenerator data={GENERAL_INFORMATION_DATA} />
        <Line ref={SELLING_GIFT_CARDS_REF} />
        <Header>
          <HeadLine headingText={"Selling Gift Cards"} />
        </Header>
        <QAGenerator data={SELLING_GIFT_CARDS_DATA} />
        <Line ref={MERCHANT_INFORMATION_REF} />
        <Header>
          <HeadLine headingText={"Merchant Information"} />
        </Header>
        <QAGenerator data={MERCHANT_INFORMATION_DATA} />
        <Line ref={ORDERS_AND_PAYMENTS_REF} />
        <Header>
          <HeadLine headingText={"Orders & Payments"} />
        </Header>
        <QAGenerator data={ORDERS_AND_PAYMENTS_DATA} />
        <>
          <Line ref={BAD_CARDS_LIABILITIES_DEDUCTIONS_REF} />
          <Header>
            <HeadLine headingText={"Bad Cards, Liabilities, & Deductions"} />
          </Header>
          <QAGenerator data={BAD_CARDS_LIABILITIES_DEDUCTIONS_DATA} />
        </>
      </Content>
    </Container>
  );
};

FAQContent.propTypes = {};

export { FAQContent };
